import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/api";
import { Spinner } from "@/components/ui-kit";
import { EmptyState, CampaignCard } from "@/components/dashboard-bits";
import { Search } from "lucide-react";
import { CATEGORY_OPTIONS, SERVICE_OPTIONS } from "@/constants/taxonomy";

const selectCls = "rounded-xl bg-white border border-[#E6E1D9] px-3.5 py-2.5 text-sm outline-none focus:border-[#292827] transition-colors appearance-none";

export default function Campaigns() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState(null);
  const [filters, setFilters] = useState({ q: "", category: "all", content_type: "all", sort: "newest" });

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (filters.q) params.set("q", filters.q);
    if (filters.category !== "all") params.set("category", filters.category);
    if (filters.content_type !== "all") params.set("content_type", filters.content_type);
    params.set("sort", filters.sort);
    const { data } = await api.get(`/campaigns?${params.toString()}`);
    setCampaigns(data);
  }, [filters]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const openDetail = (c) => navigate(`/dashboard/kampanjat/${c.campaign_id}`);
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <div data-testid="campaigns-page">
      <h3 className="text-2xl font-semibold tracking-display text-[#292827] mb-6">Kampanjat</h3>

      {/* Search + filters */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-8">
        <div className="relative flex-1">
          <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#968F85]" />
          <input value={filters.q} onChange={(e) => set("q", e.target.value)} placeholder="Hae kampanjoita..."
            data-testid="campaign-search"
            className="w-full rounded-xl bg-white border border-[#E6E1D9] pl-11 pr-4 py-3 text-[15px] outline-none focus:border-[#292827] transition-colors" />
        </div>
        <select className={selectCls} value={filters.category} onChange={(e) => set("category", e.target.value)} data-testid="filter-category">
          <option value="all">Kaikki kategoriat</option>
          {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className={selectCls} value={filters.content_type} onChange={(e) => set("content_type", e.target.value)} data-testid="filter-content-type">
          <option value="all">Kaikki palvelut</option>
          {SERVICE_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className={selectCls} value={filters.sort} onChange={(e) => set("sort", e.target.value)} data-testid="filter-sort">
          <option value="newest">Uusimmat</option>
          <option value="oldest">Vanhimmat</option>
          <option value="deadline">Hakuaika päättyy</option>
        </select>
      </div>

      {!campaigns ? <Spinner /> : campaigns.length === 0 ? (
        <EmptyState title="Ei kampanjoita hakuehdoilla" subtitle="Kokeile muuttaa hakua tai suodattimia." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {campaigns.map((c) => <CampaignCard key={c.campaign_id} c={c} onClick={() => openDetail(c)} />)}
        </div>
      )}
    </div>
  );
}
